import classNames from 'classnames';
import DOMPurify from 'dompurify';
import Moment from 'moment';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import DisplayErrorMessage from '../../../common/components/misc/DisplayErrorMessage';
import DisplayLoading from '../../../common/components/misc/DisplayLoading';
import * as CustomerActions from '../../../common/services/customer/Actions';
import * as CustomerService from '../../../common/services/customer/Service';
import * as Actions from '../../../common/services/offer/Actions';
import * as OfferService from '../../../common/services/offer/Service';
import { OfferError } from '../../../common/services/offer/Types';
import { CustomerDataDTO } from '../../../common/types/dto/CustomerDataDTO';
import { LookupValueDTO } from '../../../common/types/dto/LookupValueDTO';
import { OfferDetailsDTO } from '../../../common/types/dto/OfferDetailsDTO';
import { RequestVoucherDTO } from '../../../common/types/dto/RequestVoucherDTO';
import { VehicleDataDTO } from '../../../common/types/dto/VehicleDataDTO';
import { VoucherDTO } from '../../../common/types/dto/VoucherDTO';
import { Utils } from '../../../common/utils';
import { RootState } from '../../base/RootReducer';
import { config } from '../../common/';
import AlertMessage from '../../common/Components/AlertMessage';
import DealerDistrictSelect from '../../common/Components/DealerDistrictSelect';
import ValidateVehicle from '../../common/Components/ValidateVehicle';
import VehicleSelectV3 from '../../common/Components/VehicleSelectV3';
import { useMatch } from '../../common/RouterHooks';
import { AppThunkDispatch } from '../../common/types';

interface OfferState {
    isExpired: boolean;
    vehcode: number;
    dealerCode: number;
    staticDealerCode: number;
    dealerCodeErrorMsg: string;
    submitErrorText: string;
    submitSuccessText: string;
    vehicle: VehicleDataDTO | null;
    vehicles: VehicleDataDTO[];
    isValidationAccepted: boolean;
    isValidationNotValid: boolean;
    isValidationManualRequired: boolean;
    validationErrorReason: string;
    isValidationSubmited: boolean;
    isRefreshed: boolean;
    isVehValid: boolean;
}

export interface RouterOfferProps {
    code: number;
    vehcode?: number;
}

interface ReduxStateProps {
    isCommunicating: boolean;
    getOfferError: boolean;
    offerDetails: OfferDetailsDTO;

    getVoucherIsCommunicating: boolean;
    getVoucherError: boolean;
    voucherDetails: VoucherDTO;

    printVoucherIsCommunicating: boolean;
    printVoucher: Blob | undefined;
}

interface ReduxDispatchProps {
    offerActions: typeof Actions;
    offerServiceActions: OfferService.ServiceActions;
    dispatch: AppThunkDispatch;

    customerActions: typeof CustomerActions;
    customerServiceActions: CustomerService.ServiceActions;
    customerInfo: CustomerDataDTO | undefined;
}

type VehicleVisitsProperties = ReduxStateProps & ReduxDispatchProps;

const Offer: React.FC<VehicleVisitsProperties> = (Props) => {
    const { t } = useTranslation();
    const routeMatch = useMatch<RouterOfferProps>();

    const initialState: OfferState = {
        isExpired: false,
        vehcode: -1,
        dealerCode: 0,
        staticDealerCode: 0,
        dealerCodeErrorMsg: '',
        submitErrorText: '',
        submitSuccessText: '',
        vehicle: null,
        vehicles: [],
        isValidationAccepted: false,
        isValidationNotValid: false,
        isValidationManualRequired: false,
        validationErrorReason: '',
        isValidationSubmited: false,
        isRefreshed: false,
        isVehValid: false,
    };
    const [state, setState]: [OfferState, React.Dispatch<any>] = useReducer(
        (oldState, newState) => ({ ...oldState, ...newState }), initialState,
    );

    useEffect(() => {
        // this will run only once
        const vehcode = routeMatch.params.vehcode;
        if (vehcode) {
            setState({ vehcode });
        }
        GetOffer();
    }, []);

    async function GetOffer() {
        const code = routeMatch.params.code;
        try {
            await Props.offerServiceActions.getOffer(code);
        } catch (error) {
            let exceptionErrorMsg: string = t('exception:UnknownException');
            if (error instanceof OfferError) {
                const exceptionType: string = error.offerErrorInfo.exceptionType || 'UnknownException';
                exceptionErrorMsg = t(`exception:${exceptionType.split('.').pop()}`);
            }
            setState({ submitErrorText: exceptionErrorMsg });
            Props.dispatch(Notifications.error({
                title: t('error'),
                message: exceptionErrorMsg,
                position: 'tc',
                autoDismiss: 5,
            }));
        }
    }

    useEffect(() => {
        const interval = setInterval(() => {
            handleRefreshVehicleData();
        }, 10000);
        return () => clearInterval(interval);
    }, [state.isValidationSubmited, state.isValidationAccepted, state.isValidationNotValid, state.isValidationManualRequired, state.validationErrorReason]);

    async function handleRefreshVehicleData() {
        if (state.isValidationSubmited && !state.isValidationAccepted && !state.isValidationNotValid && !state.isValidationManualRequired) {
            const vehicleRefreshData = await Props.customerServiceActions.vehicleData(state.vehcode);
            if (vehicleRefreshData && vehicleRefreshData.vehcode === state.vehcode) {
                setState({
                    isRefreshed: true,
                    isValidationAccepted: vehicleRefreshData.isValidationAccepted,
                    isValidationNotValid: vehicleRefreshData.isValidationNotValid,
                    isValidationManualRequired: vehicleRefreshData.isValidationManualRequired,
                    validationErrorReason: vehicleRefreshData.validationErrorReason,
                    isValidationSubmited: vehicleRefreshData.isValidationSubmited,
                });
            }
        }
    }

    useEffect(() => {
        if (state.vehcode > -1) {
            GetVoucher();
        }
    }, [state.vehcode]);

    useEffect(() => {
        if (Object.keys(Props.offerDetails).length !== 0) {
            const isExpired = (Props.offerDetails.endDate && Moment(Props.offerDetails.endDate) < Moment(new Date()));
            setState({ isExpired });
        }
    }, [Props.offerDetails]);

    useEffect(() => {
        if (Props.customerInfo && Props.customerInfo.vehicles) {
            const vehTemp = state.vehicles.find((x: VehicleDataDTO) => x.vehcode === state.vehcode);
            setState({
                vehicles: Props.customerInfo.vehicles,
                vehicle: vehTemp,
                isValidationAccepted: vehTemp ? vehTemp.isValidationAccepted : false,
                isValidationNotValid: vehTemp ? vehTemp.isValidationNotValid : false,
                isValidationManualRequired: vehTemp ? vehTemp.isValidationManualRequired : false,
                validationErrorReason: vehTemp ? vehTemp.validationErrorReason : '',
                isValidationSubmited: vehTemp ? vehTemp.isValidationSubmited : false,
                isRefreshed: false,
            });
        }
    }, [Props.customerInfo, state.vehcode]);

    function GetVoucher() {
        const code = routeMatch.params.code;
        Props.offerServiceActions.getVoucher(state.vehcode, code);
        setState({
            dealerCode: 0,
            staticDealerCode: 0,
        });
    }

    useEffect(() => {
        if (Props.voucherDetails && Object.keys(Props.voucherDetails).length > 0) {
            setState({
                dealerCode: Props.voucherDetails.dealeR_CODE,
                staticDealerCode: Props.voucherDetails.dealeR_CODE,
            });
        } else if (state.dealerCode !== 0 || state.staticDealerCode !== 0) {
            setState({
                dealerCode: 0,
                staticDealerCode: 0,
            });
        }
    }, [Props.voucherDetails]);

    useEffect(() => {
        if (Props.printVoucher && !Props.printVoucherIsCommunicating && Props.voucherDetails) {
            Utils.saveData(Props.printVoucher, `${Props.voucherDetails.reF_VOUCHER}.pdf`);
        }
    }, [Props.printVoucher, Props.printVoucherIsCommunicating]);

    function onVehicleChange(vehcode: number) {
        if (state.vehcode !== vehcode) {
            const vehTemp = state.vehicles.find((x: VehicleDataDTO) => x.vehcode === vehcode);

            const isVehValid = (!Props.offerDetails.requiresSubscription && !Props.offerDetails.specialConditions) || (Props.offerDetails.filteredVehAvailable && Props.offerDetails.filteredVehAvailable.find((vCode) => vCode === vehcode));
            setState({
                vehcode,
                vehicle: vehTemp,
                submitSuccessText: '',
                submitErrorText: '',
                dealerCodeErrorMsg: '',
                dealerCode: 0,
                isValidationAccepted: vehTemp ? vehTemp.isValidationAccepted : false,
                isValidationNotValid: vehTemp ? vehTemp.isValidationNotValid : false,
                isValidationManualRequired: vehTemp ? vehTemp.isValidationManualRequired : false,
                validationErrorReason: vehTemp ? vehTemp.validationErrorReason : '',
                isValidationSubmited: vehTemp ? vehTemp.isValidationSubmited : false,
                isRefreshed: false,
                isVehValid,
            });
        }
    }

    function onDealerChange(selected: LookupValueDTO<number>) {
        if (selected) {
            setState({
                dealerCode: selected.value,
                dealerCodeErrorMsg: '',
            });
        } else {
            setState({ dealerCode: 0 });
        }
    }

    async function onValidateVehicleSuccess() {
        window.location.reload();
    }

    function validateForm() {
        let isValid: boolean = true;
        if (Props.offerDetails.showDealers && !Props.offerDetails.requiresSubscription && state.dealerCode <= 0) {
            setState({ dealerCodeErrorMsg: t('offers:dealer-error') });
            isValid = false;
        }
        return isValid;
    }

    async function onBtnRequest(e: React.MouseEvent<{}> | null) {
        if (e) {
            e.preventDefault();
        }
        if (Props.isCommunicating) {
            return;
        }

        if (!validateForm()) {
            return;
        }
        try {
            const requestVoucherDTO: RequestVoucherDTO = {
                dealer: state.dealerCode,
                vehcode: state.vehcode,
                offerCode: routeMatch.params.code,
            };
            setState({
                submitErrorText: '',
                submitSuccessText: '',
            });
            await Props.offerServiceActions.requestVoucher(requestVoucherDTO);
            Props.dispatch(Notifications.success({
                title: t('success'),
                message: t('offers:success'),
                position: 'tc',
                autoDismiss: 5,
            }));
            setState({
                submitSuccessText: t('offers:success'),
                lastModification: new Date(),
            });
            GetVoucher();
            Props.customerServiceActions.customerData();

            // Holmes Place
            if (Props.offerDetails.reference === 'CB#04') {
                window.open(config.HolmesPlaceOfferUrl, '_blank', 'noopener,noreferrer');
            }
        } catch (error) {
            let exceptionErrorMsg: string = t('exception:UnknownException');
            if (error instanceof OfferError) {
                const exceptionType: string = error.offerErrorInfo.exceptionType || 'UnknownException';
                exceptionErrorMsg = t(`exception:${exceptionType.split('.').pop()}`);
            }
            setState({ submitErrorText: exceptionErrorMsg });
            Props.dispatch(Notifications.error({
                title: t('error'),
                message: exceptionErrorMsg,
                position: 'tc',
                autoDismiss: 5,
            }));
        }
    }

    async function onPrintRequest(e: React.MouseEvent<{}> | null) {
        if (e) {
            e.preventDefault();
        }
        if (Props.printVoucherIsCommunicating) {
            return;
        }

        try {
            const code = routeMatch.params.code;
            const voucherBlob = await Props.offerServiceActions.printVoucher(state.vehcode, code);
            if (!voucherBlob) {
                throw new Error('result is Null');
            }
            Props.dispatch(Notifications.success({
                title: t('success'),
                message: t('offers:success-print'),
                position: 'tc',
                autoDismiss: 5,
            }));
        } catch (error) {
            let exceptionErrorMsg: string = t('exception:UnknownException');
            if (error instanceof OfferError) {
                const exceptionType: string = error.offerErrorInfo.exceptionType || 'UnknownException';
                exceptionErrorMsg = t(`exception:${exceptionType.split('.').pop()}`);
            }
            setState({ submitErrorText: exceptionErrorMsg });
            Props.dispatch(Notifications.error({
                title: t('error'),
                message: exceptionErrorMsg,
                position: 'tc',
                autoDismiss: 5,
            }));
        }
        return;
    }

    const MyVoucher: React.FC = () => {
        const lastUsage = Props.offerDetails.requiresSubscription && state.vehicle && state.vehicle.vehicleSubscriptionUsages && state.vehicle.vehicleSubscriptionUsages.find((x) => x.subscriptionItemCode === Props.offerDetails.subscriptionItemCode);
        if ((!Props.getVoucherIsCommunicating && Props.voucherDetails && Object.keys(Props.voucherDetails).length > 0) && (!Props.offerDetails.requiresSubscription || lastUsage)) {
            return (
                <React.Fragment>
                    <div className={classNames(['cell', 'medium-12'])}>
                        <hr />
                    </div>
                    <div className={classNames(['cell', 'medium-12'])}>
                        <p className={'text-bold'}>
                            {t('offers:voucher-title')}
                        </p>
                        <div className={'cell'}>
                            <div className={classNames(['grid-x', 'grid-margin-x', 'grid-margin-y'])}>
                                <div className={classNames(['cell', 'large-3', 'medium-3', 'small-6'])}>
                                    <label className={'form-label'} htmlFor="state">{t('offers:voucher-state')}</label>
                                    <div id="state">{Props.voucherDetails.status}</div>
                                </div>
                                <div className={classNames(['cell', 'large-3', 'medium-3', 'small-6'])}>
                                    <label className={'form-label'} htmlFor="referece">{t('offers:voucher-referece')}</label>
                                    <div id="referece">{Props.voucherDetails.reF_VOUCHER}</div>
                                </div>
                                <div className={classNames(['cell', 'large-3', 'medium-3', 'small-6'])}>
                                    <label className={'form-label'} htmlFor="dreg">{t('offers:voucher-dreg')}</label>
                                    <div id="dreg">{Props.voucherDetails.dreg ? Moment(Props.voucherDetails.dreg).format('YYYY-MM-DD') : null}</div>
                                </div>
                                <div className={classNames(['cell', 'large-3', 'medium-3', 'small-6'])}>
                                    <label className={'form-label'} htmlFor="dexpire">{t('offers:voucher-dexpire')}</label>
                                    <div id="dexpire">{Props.voucherDetails.dexpire ? Moment(Props.voucherDetails.dexpire).format('YYYY-MM-DD') : Props.voucherDetails.dreg ? Moment(Props.voucherDetails.dreg).add(6, 'M').format('YYYY-MM-DD') : null}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            );
        }
        return null;
    };

    interface SubmitFormProps {
        isInvalid: boolean;
    }
    function SubmitForm(props: SubmitFormProps) {
        if (Props.offerDetails.vehValidationRequired) {
            if (config.EnableVehicleValidation && !Props.offerDetails.requiresSubscription && (state.vehicle && !state.isValidationSubmited)) {
                return null;
            } else if (!Props.offerDetails.requiresSubscription && config.EnableVehicleValidation && state.vehicle && state.isValidationManualRequired) {
                // validationErrorReason
                return (
                    <React.Fragment>
                        <AlertMessage msg={t('profile:validateVehManualRequired')} type={'info'} />
                        {state.validationErrorReason &&
                            <AlertMessage msg={t('notification')} type={'info'}>
                                <React.Fragment>
                                    <br />
                                    {state.validationErrorReason}
                                </React.Fragment>
                            </AlertMessage>}
                    </React.Fragment>
                );
            } else if (!Props.offerDetails.requiresSubscription && config.EnableVehicleValidation && state.vehicle && !state.isValidationNotValid && !state.isValidationAccepted) {
                return (
                    <AlertMessage msg={t('profile:validateVehSubmited')} type={'info'} />
                );
            } else if (!Props.offerDetails.requiresSubscription && config.EnableVehicleValidation && state.vehicle && state.isValidationNotValid) {
                return (
                    <AlertMessage msg={t('profile:validateVehNotValid')} type={'warning'} />
                );
            }
        }

        // Subscription -------------------
        const lastUsage = Props.offerDetails.requiresSubscription && state.vehicle && state.vehicle.vehicleSubscriptionUsages && state.vehicle.vehicleSubscriptionUsages.find((x) => x.subscriptionItemCode === Props.offerDetails.subscriptionItemCode);
        const isCooldownReady = lastUsage && lastUsage.subscriptionItemFrequency && lastUsage.subscriptionItemFrequencyAmount
            && Moment(lastUsage.usageDate).add(lastUsage.subscriptionItemFrequency * 30 / lastUsage.subscriptionItemFrequencyAmount, 'days') <= Moment(new Date());
        const shouldRequestNew = Props.offerDetails.requiresSubscription && isCooldownReady;

        const loadingStr = t('loadingText');
        const requestStr = t('offers:request');
        const printStr = t('offers:print');

        const errors = (<React.Fragment>
            <div className="error-message">{state.dealerCodeErrorMsg}</div>
            <div className="error-message">{state.submitErrorText}</div>
        </React.Fragment>);

        return (
            <React.Fragment>
                {!props.isInvalid &&
                    <React.Fragment>
                        <div className={'button-group'}>
                            {shouldRequestNew || ((!Props.getVoucherIsCommunicating && state.staticDealerCode === 0) && (!state.isExpired || (state.isExpired && state.staticDealerCode !== 0))) ?
                                <div>
                                    {errors}
                                    <input
                                        type="button"
                                        className={classNames(['button', 'bttSubscrever'])}
                                        onClick={onBtnRequest}
                                        disabled={Props.isCommunicating}
                                        value={Props.isCommunicating ? loadingStr : requestStr}
                                    />
                                </div>
                                : ''}
                            {(!Props.offerDetails.requiresSubscription || !shouldRequestNew) && (!Props.getVoucherIsCommunicating && state.staticDealerCode !== 0) ?
                                <div>
                                    {errors}
                                    {Props.offerDetails.reference === 'CB#04' ?
                                        (<AlertMessage msg={t('offers:already-requested-voucher')} type={'secondary'} />) :
                                        <input
                                            type="button"
                                            className={classNames(['button', 'bttSubscrever'])}
                                            onClick={onPrintRequest}
                                            disabled={Props.printVoucherIsCommunicating}
                                            value={Props.printVoucherIsCommunicating ? loadingStr : printStr}
                                        />}
                                </div>
                                : ''}
                        </div>
                        {state.submitSuccessText && <AlertMessage msg={state.submitSuccessText} type={'secondary'} />}
                    </React.Fragment>
                }
                {props.isInvalid &&
                    <div className="error-message">{t('offers:invalid-veh')}</div>
                }
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {!Props.getOfferError && !Props.isCommunicating && Object.keys(Props.offerDetails).length !== 0 ? (
                <div className={classNames(['grid-container', 'fluid', 'margin-top-2'])}>
                    <div className={classNames(['cell', 'medium-12'])}>
                        <p className={'text-bold'}>{Props.offerDetails.title}</p>
                        <div dangerouslySetInnerHTML={{ __html: Props.offerDetails.description ? DOMPurify.sanitize(Props.offerDetails.description, { ADD_ATTR: ['target'] }) : '' }} />
                    </div>
                    <div className={classNames(['cell', 'medium-12'])}>
                        <hr />
                    </div>
                    <div className={classNames(['cell', 'medium-12'])}>
                        <p className={'text-bold'}>{t('offers:conditions')}</p>
                        <div dangerouslySetInnerHTML={{ __html: Props.offerDetails.conditions ? DOMPurify.sanitize(Props.offerDetails.conditions, { ADD_ATTR: ['target'] }) : '' }} />
                    </div>
                    <div className={classNames(['cell', 'medium-12'])}>
                        <hr />
                    </div>
                    {Props.offerDetails.showVoucher &&
                        <React.Fragment>
                            <div className={classNames(['cell', 'medium-12'])}>
                                <div className={classNames(['grid-x', 'grid-margin-x', 'align-justify'])}>
                                    <div className={classNames(['columns', 'medium-6', 'small-12'])}>
                                        <p className={'text-bold'}>
                                            {t('offers:select-vehicle')}
                                        </p>
                                        <VehicleSelectV3 onChange={onVehicleChange} isMini={true} defaultVehcode={state.vehcode} />
                                    </div>

                                    <div className={classNames(['columns', 'medium-6', 'small-12'])}>
                                        {!state.isExpired || (state.isExpired && state.staticDealerCode !== 0)
                                            ? (Props.offerDetails && !Props.offerDetails.requiresSubscription
                                                ? (Props.offerDetails.showDealers
                                                    ? <p className={'text-bold'}>{t('offers:select-dealer')}</p>
                                                    : '')
                                                : '')
                                            : <p className={'text-bold'}>{t('offers:offer-expired')}</p>}
                                        {Props.offerDetails && !Props.offerDetails.requiresSubscription && Props.offerDetails.showDealers &&
                                            <DealerDistrictSelect
                                                validation={!Props.getVoucherIsCommunicating && (!state.isExpired || (state.isExpired && state.staticDealerCode !== 0))}
                                                onChange={onDealerChange}
                                                staticDealerCode={state.staticDealerCode}
                                                disbaleSelectorOnSubmit={true}
                                                sideBySideSelectorsDisplay={false}
                                            />}
                                        <SubmitForm isInvalid={!state.isVehValid} />
                                    </div>
                                </div>
                            </div>

                            <ValidateVehicle
                                validation={Props.offerDetails.vehValidationRequired && config.EnableVehicleValidation && !Props.offerDetails.requiresSubscription && (!(!state.vehicle || (state.isValidationSubmited && !state.isValidationNotValid) || state.isExpired) || state.isValidationManualRequired)}
                                vehcode={state.vehcode}
                                onSucess={onValidateVehicleSuccess}
                                header={<p className={'text-bold'}>{t('profile:subTitleEdit4')}</p>}
                                className={'margin-top-2'}
                            />
                        </React.Fragment>
                    }
                    <DisplayLoading validation={Props.getVoucherIsCommunicating} />
                    <MyVoucher />
                </div>
            ) : null}

            <DisplayErrorMessage validation={Props.getOfferError} messageTxt={state.submitErrorText} />
            <DisplayLoading validation={Props.isCommunicating || (Object.keys(Props.offerDetails).length === 0 && !Props.getOfferError)} />
        </React.Fragment>
    );
};

const mapStateToProps = (store: RootState) => ({
    isCommunicating: store.offerState.getOffer.isCommunicating,
    getOfferError: store.offerState.getOffer.error,
    offerDetails: store.offerState.getOffer.data,

    getVoucherError: store.offerState.getVoucher.error,
    getVoucherIsCommunicating: store.offerState.getVoucher.isCommunicating,
    voucherDetails: store.offerState.getVoucher.data,

    printVoucherIsCommunicating: store.offerState.printVoucher.isCommunicating,
    printVoucher: store.offerState.printVoucher.data,

    customerInfo: store.customerState.customerData.data.customerData,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    offerActions: bindActionCreators(Actions, dispatch),
    offerServiceActions: OfferService.bindServiceActionCreators(config, dispatch),
    dispatch,

    customerActions: bindActionCreators(CustomerActions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Offer);
